import { useEffect, lazy, Suspense, type ComponentType } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { recordClientContext } from './api/activity';
import { useSubjects } from '@shared-ui/reference-data';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { QuestionSessionProvider } from './context/QuestionSessionContext';
import { CanvasDebugOverlay } from './components/canvas/fabricjs/CanvasDebugOverlay';
import { CanvasTelemetryMount } from './components/canvas/fabricjs/CanvasTelemetryMount';
import Login from './pages/Login';
import Landing from './pages/landing';
// The student page is a front door few visitors open; it loads when one does.
const StudentLanding = lazy(() => import('./pages/landing/StudentLanding'));
// The labs are loaded when opened: the benches are most of the app's code by
// weight, and a student who never opens one should not download them.
const ChemistryLabPage = lazyLab(() => import('./pages/ChemistryLabPage'));
const MechanicsBenchPage = lazyLab(() => import('./pages/MechanicsBenchPage'));
const OpticsBenchPage = lazyLab(() => import('./pages/OpticsBenchPage'));
const ChemistryLabExperimentsPage = lazyLab(() => import('./pages/ChemistryLabExperimentsPage'));
const ChemistryLabExperimentPage = lazyLab(() => import('./pages/ChemistryLabExperimentPage'));
const WhatsWrongPage = lazy(() => import('./pages/WhatsWrong'));
const BenchSetupsPage = lazyLab<{ bench: 'mechanics' | 'optics' }>(() => import('./pages/labs/BenchSetupsPage'));
const LabSimulationsPage = lazyLab<object>(() => import('./pages/labs/LabSimulations').then(m => ({ default: m.LabSimulationsPage })));
const LabSimulationDetailPage = lazyLab<object>(() => import('./pages/labs/LabSimulations').then(m => ({ default: m.LabSimulationDetailPage })));
// Straight from its file: the components barrel would bring every component
// (and KaTeX) into the first download.
import { SessionExpiredDialog } from '@shared-ui/components/SessionExpiredDialog';
import { ServerMessages } from './messages/ServerMessages';
import { DuelRequestPopup } from './messages/DuelRequestPopup';
import { PagePerfTracker } from '@shared-ui/logging';
import { UserActionTracker } from '@shared-ui/user-actions';
import { WhatsNewProvider } from '@shared-ui/whats-new';
import './whatsNewExperiences'; // registers this app's custom What's New experiences (side effect)
import { ShellSync } from './shell/ShellSync';
import { page } from './appPagesLoader';

// Past the front door, every page comes from one lazily loaded chunk (see
// appPages.ts): a visitor to the landing page or the sign-in screen does not
// download them.
const AppLayout = page('AppLayout');
const Home = page('Home');
const Practice = page('Practice');
const Worksheets = page('Worksheets');
const Subject = page('Subject');
const Plan = page('Plan');
const CurriculumProgress = page('CurriculumProgress');
const StudentPlacement = page('StudentPlacement');
const DayDetail = page('DayDetail');
const Welcome = page('Welcome');
const BattleEntry = page('BattleEntry');
const BattleHistory = page('BattleHistory');
const BattlePage = page('BattlePage');
const BattleShell = page('BattleShell');
const FriendsList = page('FriendsList');
const Leaderboard = page('Leaderboard');
const Impersonate = page('Impersonate');
const CompleteRegistration = page('CompleteRegistration');
const SessionPage = page('SessionPage');
const SessionHost = page('SessionHost');
const ChapterPracticePage = page('ChapterPracticePage');
const PracticeLinkLanding = page('PracticeLinkLanding');
const ChapterPracticeEntry = page('ChapterPracticeEntry');
const PracticeFind = page('PracticeFind');
const LinkEnded = page('LinkEnded');
const ExamInstructionsPage = page('ExamInstructionsPage');
const FirstRun = page('FirstRun');
const Feedback = page('Feedback');
const AttemptDetail = page('AttemptDetail');
const StickingPatternDetail = page('StickingPatternDetail');
const Settings = page('Settings');
const MyTeachers = page('MyTeachers');
const Subscription = page('Subscription');
const QuestionSets = page('QuestionSets');
const QuestionSetDetail = page('QuestionSetDetail');
const QuestionSetCreate = page('QuestionSetCreate');
const PaperCheckNew = page('PaperCheckNew');
const PaperCheckProgress = page('PaperCheckProgress');
const TestResults = page('TestResults');
const Assignments = page('Assignments');
const AssignmentDetail = page('AssignmentDetail');
const AssessmentReport = page('AssessmentReport');
const BloomAnalysisPage = page('BloomAnalysisPage');
const BloomTaxonomyPage = page('BloomTaxonomyPage');
const BookmarksPage = page('BookmarksPage');
const PhotoUpload = page('PhotoUpload');
const PublicProgressReportPage = page('PublicProgressReportPage');
const BulkPhotoUpload = page('BulkPhotoUpload');
const Messages = page('Messages');
const ConceptLabPage = page('ConceptLabPage');
const KnowledgeMapPage = page('KnowledgeMapPage');
const Labs = page('Labs');
const SharedWithMePage = page('SharedWithMePage');
const LearningMomentsV2Page = page('LearningMomentsV2Page');
const ConceptRevisionPage = page('ConceptRevisionPage');
const LearningMomentDetailPage = page('LearningMomentDetailPage');

/** A lab page, split into its own chunk and shown behind a quiet placeholder
 *  while it loads. */
function lazyLab<P extends object>(load: () => Promise<{ default: ComponentType<P> }>) {
  const Page = lazy(load);
  return function LazyLab(props: P) {
    return (
      <Suspense fallback={<div className="app-lab-loading" aria-busy="true">Opening the lab…</div>}>
        <Page {...props} />
      </Suspense>
    );
  };
}

function StudentBookmarks() {
  const navigate = useNavigate();
  // Wrapper scopes the centered 720px layout + title font to the student app
  // (BookmarksPage is shared with teacher/admin, whose layouts stay as-is).
  return (
    <div className="app-bookmarks-wrap">
      <BookmarksPage
        onQuestionClick={(id) => navigate(`/question/${id}`, { state: { backLabel: 'Bookmarks', backPath: '/bookmarks' } })}
        onSubmissionClick={(id) => navigate(`/attempts/${id}`, { state: { backLabel: 'Bookmarks', backPath: '/bookmarks' } })}
      />
    </div>
  );
}

/**
 * Warms the subject catalogue as soon as there's a session.
 *
 * Subject pickers and Practice both render from the catalogue, and it's
 * fetched once per 30-min staleTime and cached in localStorage — fetching it
 * up front means no picker renders against a cold cache and falls back to the
 * code list. Mounted only when authenticated: `fetchApi` throws
 * "Not authenticated" without a token, so calling it earlier just queues a
 * failing query.
 */
/** Redirect to `to`, keeping the current query string (plus `extra`). */
function RedirectKeepingQuery({ to, extra }: { to: string; extra?: string }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (extra) new URLSearchParams(extra).forEach((v, k) => params.set(k, v));
  const qs = params.toString();
  return <Navigate to={`${to}${qs ? `?${qs}` : ''}`} replace />;
}

function SubjectCatalogueWarmer() {
  useSubjects();
  return null;
}

function App() {
  // The new IA ("new experience") is now the only experience — the old
  // flow and its `new_experience` feature gate have been removed.
  const { user } = useAuth();

  // Device/viewport telemetry — one beacon per session once authenticated.
  useEffect(() => { if (user) recordClientContext(); }, [user]);

  return (
    <QuestionSessionProvider>
      <SessionExpiredDialog />
      <ServerMessages />
      <DuelRequestPopup />
      <PagePerfTracker />
      <UserActionTracker userId={user?.id} appSource="web-student" />
      {user && <SubjectCatalogueWarmer />}
      <ShellSync />
      <CanvasTelemetryMount userId={user?.id} />
      <CanvasDebugOverlay />
      <WhatsNewProvider appTarget="student" userId={user?.id}>
      <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/students" element={<StudentLanding />} />
        {/* Public parent report (WhatsApp "View Full Report" link) — login-free. */}
        <Route path="/progress-report/:reportId" element={<PublicProgressReportPage />} />
        <Route path="/upload/:sessionId" element={<PhotoUpload />} />
        <Route path="/bulk-upload/:sessionId" element={<BulkPhotoUpload />} />
        {/* Where a partner's link in a video description lands. Public: the
            student who followed it has no account yet, and meeting a login
            screen before the questions is what makes every bounce invisible
            (CP-1, CP-33). */}
        <Route path="/p/:slug" element={<PracticeLinkLanding />} />
        {/* Where the redirector sends a follower, and where a link that no
            longer resolves sends them. Both public: whoever followed the URL
            may have no account, and a login screen before the questions is
            what makes every bounce invisible (CP-1, CP-33). */}
        <Route path="/practice/:slug" element={<ChapterPracticeEntry />} />
        <Route path="/link-ended" element={<LinkEnded />} />
        <Route path="/whatswrong" element={<Suspense fallback={null}><WhatsWrongPage /></Suspense>} />
        <Route path="/whatswrong/:id" element={<Suspense fallback={null}><WhatsWrongPage /></Suspense>} />
        <Route path="/login" element={<Login />} />
        <Route path="/impersonate" element={<Impersonate />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/complete-registration" element={<CompleteRegistration />} />
          <Route path="/first-run" element={<FirstRun />} />
          {/* Chapter Practice brings its own header (CP-30), so it sits outside
              AppLayout: the full app's nav, streak and prompts are precisely
              what a student who followed a partner link must not meet (CP-3). */}
          {/* Driven by the student's curriculum for a subject. Declared BEFORE
              `/practice/:slug`, or `subject` matches as a slug and every one of
              these resolves to a link that does not exist. Signed in only — no
              link was published, so there is nobody to preview to. */}
          {/* A simulation's practice set, or any practice set by its id — found
              and handed to its link, `/practice/<slug>`. Signed in: the science
              site's "Practise" button asks for an account first. */}
          <Route path="/practice/sim/:simId" element={<PracticeFind by="simulation" />} />
          <Route path="/practice/set/:setId" element={<PracticeFind by="set" />} />
          <Route path="/practice/subject/:subject" element={<ChapterPracticePage />} />
          <Route path="/practice/subject/:subject/:chapterKey" element={<ChapterPracticePage />} />
          <Route path="/practice/subject/:subject/:chapterKey/q/:n" element={<ChapterPracticePage />} />
          <Route path="/practice/:slug/q/:n" element={<ChapterPracticePage />} />
          <Route element={<AppLayout />}>
            {/* Guest FRE win — rendered inside the shell (sidebar). */}
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/home" element={<Home />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/curriculum-progress" element={<CurriculumProgress />} />
            {/* Keeps the original (elegant) Assignments page at /assigned. */}
            <Route path="/assigned" element={<Assignments />} />
            <Route path="/practice" element={<Practice />} />
            <Route path="/worksheets" element={<Worksheets />} />
            {/* "You" reuses the shared Settings page (tabbed SettingsLayout). */}
            <Route path="/you" element={<Settings />} />
            <Route path="/student-placement" element={<StudentPlacement />} />
            <Route path="/battle" element={<BattleShell><BattleEntry /></BattleShell>} />
            <Route path="/battle/friends" element={<FriendsList />} />
            <Route path="/battle/leaderboard" element={<Leaderboard />} />
            {/* A destination like Friends and Leaderboard, so deliberately NOT inside
                <BattleShell> — the shell is for the transient screens of one match. */}
            <Route path="/battle/history" element={<BattleHistory />} />
            <Route path="/battle/:duelId" element={<BattleShell><BattlePage /></BattleShell>} />
            <Route path="/plan/day/:date" element={<DayDetail />} />
            <Route path="/subject/:id" element={<Subject />} />
            <Route path="/question/:id" element={<SessionPage />} />
            <Route path="/feedback/:id" element={<Feedback />} />
            <Route path="/attempts/:submissionId" element={<AttemptDetail />} />
            <Route path="/sticking-patterns/:patternType/:patternKey" element={<StickingPatternDetail />} />
            {/* Redirect old learning-level route to settings */}
            <Route path="/learning-level" element={<Navigate to="/settings" replace />} />
            <Route path="/my-teachers" element={<MyTeachers />} />
            {/* Plan, today's use, and buying Plus / Pro (web only). billing target §11. */}
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/question-sets" element={<QuestionSets />} />
            <Route path="/question-sets/create" element={<QuestionSetCreate />} />
            <Route path="/question-sets/:id" element={<QuestionSetDetail />} />
            <Route path="/paper-check/new" element={<PaperCheckNew />} />
            <Route path="/paper-check/:id" element={<PaperCheckProgress />} />
            <Route path="/test-library" element={<Navigate to="/question-sets" replace />} />
            {/* Retired: the standalone Daily Progress page — its content moved into
                /plan's "Past" tab. Redirect keeps old links (and the native shell's
                baked chrome mirror) landing on Progress rather than home. */}
            <Route path="/daily-progress" element={<Navigate to="/plan?tab=past" replace />} />
            <Route path="/assignments" element={<Assignments />} />
            <Route path="/assignments/:id" element={<AssignmentDetail />} />
            {/* The instructions are read BEFORE the session exists, so the clock
                (derived from `startedAt`) does not run while they are open. */}
            <Route path="/exam/:questionSetId/instructions" element={<ExamInstructionsPage />} />
            {/* SessionHost picks the screen from the session's frozen `screen`. */}
            <Route path="/session/:sessionId" element={<SessionHost />} />
            <Route path="/results/:sessionId" element={<TestResults />} />
            <Route path="/assessments" element={<AssessmentReport />} />
            <Route path="/bloom-analysis" element={<BloomAnalysisPage />} />
            <Route path="/learning-moments" element={<LearningMomentsV2Page />} />
            <Route path="/learning-moments/:id" element={<LearningMomentDetailPage />} />
            <Route path="/revise/:conceptId" element={<ConceptRevisionPage />} />
            <Route path="/bookmarks" element={<StudentBookmarks />} />
            <Route path="/blooms-taxonomy" element={<BloomTaxonomyPage />} />
            <Route path="/messages" element={<Messages />} />
            {/* Old homes of the two Messages tabs. `?thread=` rides along from the reply pop-up. */}
            <Route path="/app-feedback" element={<RedirectKeepingQuery to="/messages" extra="tab=team" />} />
            <Route path="/help-queries" element={<RedirectKeepingQuery to="/messages" />} />
            <Route path="/concept-lab/:submissionId" element={<ConceptLabPage />} />
            <Route path="/knowledge-map" element={<KnowledgeMapPage />} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/shared" element={<SharedWithMePage />} />
            <Route path="/labs/simulations" element={<LabSimulationsPage />} />
            <Route path="/labs/simulations/:id" element={<LabSimulationDetailPage />} />
            <Route path="/chemistry-lab" element={<ChemistryLabPage />} />
            {/* The quantitative bench. A route rather than a query parameter
                because the mode is decided on the way in and fixed for the
                session — and because `?quantities=1` was a display toggle for a
                mode that did not exist yet.
                Offered from the Labs page as Stoichiometry, to a student with
                the `chemistry_lab_quantitative` gate — the server refuses a
                quantitative session to anyone else. */}
            <Route path="/chemistry-lab/quantitative" element={<ChemistryLabPage quantitative />} />
            <Route path="/mechanics-bench" element={<MechanicsBenchPage />} />
            <Route path="/mechanics-bench/setups" element={<BenchSetupsPage bench="mechanics" />} />
            <Route path="/optics-bench/setups" element={<BenchSetupsPage bench="optics" />} />
            <Route path="/optics-bench" element={<OpticsBenchPage />} />
            <Route path="/chemistry-lab/experiments" element={<ChemistryLabExperimentsPage />} />
            <Route path="/chemistry-lab/experiments/:experimentId" element={<ChemistryLabExperimentPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </WhatsNewProvider>
    </QuestionSessionProvider>
  );
}

export default App;
